/**
 * record-procedures/api — katalog procedur kartotek: cyklu życia (control/saved/delete) oraz
 * przycisku SQL na formularzu rekordu (button), Ustawienia → „Procedury SQL" → zakładka
 * „Kartoteki" (mirror `lib/processes/procedures-api.ts` / `lib/additional-views/api.ts`).
 *
 * Katalog jest TENANT-GLOBALNY (bez `company_id`) — JEDNO uprawnienie
 * `crm_core.record_procedures.manage` bramkuje wszystko (podgląd, CRUD, test, restore) —
 * patrz `useCanManageRecordProcedures`. BEZ `withRequireModule` — `crm_core` jest pakietem
 * bazowym, zawsze aktywnym (mirror widoków dodatkowych, inaczej niż procedury procesów).
 *
 * Endpointy (`/api/settings/record-procedures`, NIE pod `/processes` — mirror ścieżek
 * widoków dodatkowych):
 *   GET    /                — lista (filtry `q` ILIKE name, `recordType`, `lifecycleEvent`,
 *                              `include_deleted`)
 *   POST   /                — utwórz procedurę (BEZ `kind` — backend wyprowadza z lifecycleEvent;
 *                              `timeoutMinutes` tylko dla `button`, przy innych zdarzeniach
 *                              pole MUSI być pominięte — także `null` daje 422)
 *   GET    /:id              — szczegóły
 *   PATCH  /:id              — edycja meta/treści (`recordType`/`lifecycleEvent`/`kind`
 *                              NIEEDYTOWALNE — backend ich w ogóle nie przyjmuje;
 *                              `timeoutMinutes` dla procedury innej niż `button` → 400
 *                              TIMEOUT_NOT_ALLOWED)
 *   DELETE /:id              — soft-delete (BEZ guardu „in use" — wiązanie do slotu żyje
 *                              wprost w wierszu)
 *   POST   /:id/restore      — undelete
 *   POST   /test-sql         — próbne wykonanie (BEZ [id] — działa też podczas tworzenia)
 *
 * Dwa partial-unique po stronie backendu: `name` (→ 409 NAME_TAKEN) i slot
 * `(record_type, lifecycle_event)` (→ 409 SLOT_TAKEN) — obie kolizje możliwe przy
 * create/update-name/restore. Slot NIE obejmuje zdarzenia `button` — jedna kartoteka może mieć
 * wiele procedur „Na przycisk".
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';

import { api } from '@/lib/api';

// ============================================================================
// Types
// ============================================================================

/**
 * Moment uruchomienia procedury kartoteki (IMMUTABLE po utworzeniu): trzy momenty cyklu życia
 * zapisu (control/saved/delete) albo ręczne uruchomienie z przycisku SQL na formularzu (button).
 */
export type RecordLifecycleEvent = 'control' | 'saved' | 'delete' | 'button';

/**
 * Wyprowadzany serwerowo z `lifecycleEvent` (control/delete → validation; saved/button → action).
 */
export type RecordProcedureKind = 'validation' | 'action';

/** Granice limitu czasu procedury „Na przycisk" w minutach (mirror walidatora backendu). */
export const BUTTON_TIMEOUT_MINUTES_MIN = 1;
export const BUTTON_TIMEOUT_MINUTES_MAX = 60;
/** Wartość startowa pola „Limit czasu" — backend przy braku pola też zapisuje 1. */
export const BUTTON_TIMEOUT_MINUTES_DEFAULT = 1;

/** Mirror `DictionarySourceParam` / `ProcedureParamType` (`processes/procedures-api.ts`). */
export type RecordProcedureParamType = 'text' | 'int' | 'decimal' | 'date' | 'bool';

/** Wszystkie typy parametru — kolejność = kolejność opcji w edytorze listy parametrów SQL. */
export const RECORD_PROCEDURE_PARAM_TYPES: readonly RecordProcedureParamType[] = [
  'text',
  'int',
  'decimal',
  'date',
  'bool',
] as const;

export interface RecordProcedureParam {
  name: string;
  type: RecordProcedureParamType;
  required: boolean;
}

export interface RecordProcedure {
  id: string;
  name: string;
  description: string | null;
  kind: RecordProcedureKind;
  sql: string;
  declaredParams: RecordProcedureParam[];
  recordType: string;
  lifecycleEvent: RecordLifecycleEvent;
  /** Limit czasu w minutach — tylko dla `button`, dla pozostałych zdarzeń `null`. */
  timeoutMinutes: number | null;
  createdAt?: string;
  updatedAt?: string;
  gcrecord?: string | null;
}

/**
 * Typy kartotek dostępne w pickerze. `contractor` (kontrahent, bez podklasy) i ujednolicony
 * `document` — JEDEN typ kartoteki obejmujący wszystkie 7 klas dokumentów (faktura
 * zakupu/sprzedaży, list, wewnętrzny, umowa, handlowy, specjalny) — mają wpięte wszystkie cztery
 * momenty (silnik `lib/record-hooks/`). Konkretna klasa dokumentu dociera do procedury przez
 * zarezerwowany parametr `:record_class` (odpowiednik starego plpgsql `p_class`) — NIE przez
 * osobny `record_type` per klasa. Zadania (`task`) — też wszystkie cztery momenty, bez klasy
 * (typ zadania procedura czyta z `:fields.taskTypeId`).
 *
 * Kontakty kontrahentów, pracownicy, użytkownicy i towary (`buttonOnly`) mają wyłącznie
 * zdarzenie „Na przycisk" — ich zapis nie uruchamia procedur control/saved/delete, więc
 * katalog nie oferuje tam martwych procedur (serwer odrzuca inne zdarzenia z 422).
 *
 * Backend przyjmuje otwarty string (walidowany regexem kształtu i regułą typów przycisku), ale UI
 * ogranicza wybór do jawnie wspieranej listy — dodanie kolejnego typu kartoteki to dopisanie
 * jednego wiersza tutaj, bez zmiany reszty formularza/listy.
 */
export const RECORD_TYPE_OPTIONS: readonly {
  value: string;
  labelKey: string;
  buttonOnly: boolean;
}[] = [
  { value: 'contractor', labelKey: 'recordProcedures.recordType.contractor', buttonOnly: false },
  { value: 'document', labelKey: 'recordProcedures.recordType.document', buttonOnly: false },
  { value: 'task', labelKey: 'recordProcedures.recordType.task', buttonOnly: false },
  {
    value: 'contractor_contact',
    labelKey: 'recordProcedures.recordType.contractor_contact',
    buttonOnly: true,
  },
  { value: 'employee', labelKey: 'recordProcedures.recordType.employee', buttonOnly: true },
  { value: 'user', labelKey: 'recordProcedures.recordType.user', buttonOnly: true },
  { value: 'product', labelKey: 'recordProcedures.recordType.product', buttonOnly: true },
] as const;

/** Czy dla typu kartoteki dostępne jest wyłącznie zdarzenie „Na przycisk". */
export function isButtonOnlyRecordType(recordType: string): boolean {
  return RECORD_TYPE_OPTIONS.some((opt) => opt.value === recordType && opt.buttonOnly);
}

/** Cztery momenty uruchomienia procedury — jedyne wartości akceptowane przez backend (Zod enum). */
export const LIFECYCLE_EVENT_OPTIONS: readonly {
  value: RecordLifecycleEvent;
  labelKey: string;
}[] = [
  { value: 'control', labelKey: 'recordProcedures.lifecycleEvent.control' },
  { value: 'saved', labelKey: 'recordProcedures.lifecycleEvent.saved' },
  { value: 'delete', labelKey: 'recordProcedures.lifecycleEvent.delete' },
  { value: 'button', labelKey: 'recordProcedures.lifecycleEvent.button' },
] as const;

interface ListResponse {
  data: RecordProcedure[];
}

interface DetailResponse {
  data: RecordProcedure;
}

// ============================================================================
// Query keys
// ============================================================================

export const RECORD_PROCEDURES_LIST_KEY = ['record-procedures'] as const;
export const recordProcedureDetailKey = (id: string) => ['record-procedures', id] as const;

// ============================================================================
// Hooks — list
// ============================================================================

export interface ListRecordProceduresParams {
  q?: string;
  recordType?: string;
  lifecycleEvent?: RecordLifecycleEvent;
  includeDeleted?: boolean;
}

export function useRecordProceduresList(params: ListRecordProceduresParams = {}) {
  const { q, recordType, lifecycleEvent, includeDeleted } = params;
  return useQuery<RecordProcedure[]>({
    queryKey: [
      ...RECORD_PROCEDURES_LIST_KEY,
      {
        q: q ?? '',
        recordType: recordType ?? null,
        lifecycleEvent: lifecycleEvent ?? null,
        includeDeleted: includeDeleted ?? false,
      },
    ] as const,
    queryFn: async () => {
      const search = new URLSearchParams();
      if (q && q.trim()) search.set('q', q.trim());
      if (recordType) search.set('recordType', recordType);
      if (lifecycleEvent) search.set('lifecycleEvent', lifecycleEvent);
      if (includeDeleted) search.set('include_deleted', 'true');
      const qs = search.toString();
      const res = await api.get<ListResponse>(`/settings/record-procedures${qs ? `?${qs}` : ''}`);
      return res.data.data;
    },
    retry: false,
  });
}

function useInvalidateRecordProcedures() {
  const queryClient = useQueryClient();
  return (id?: string) => {
    queryClient.invalidateQueries({ queryKey: RECORD_PROCEDURES_LIST_KEY });
    if (id) queryClient.invalidateQueries({ queryKey: recordProcedureDetailKey(id) });
  };
}

// ============================================================================
// Hooks — mutations (create/update/delete/restore)
// ============================================================================

/**
 * `kind` NIE jest polem inputu — backend go wyprowadza serwerowo z `lifecycleEvent`.
 * `timeoutMinutes` (1–60) wolno wysłać wyłącznie przy `lifecycleEvent === 'button'` (brak pola →
 * serwer zapisuje 1); przy innych zdarzeniach pole MUSI być pominięte — także `null` daje 422.
 */
export interface CreateRecordProcedureInput {
  name: string;
  description?: string | null;
  recordType: string;
  lifecycleEvent: RecordLifecycleEvent;
  sql: string;
  declaredParams: RecordProcedureParam[];
  timeoutMinutes?: number;
}

export function useCreateRecordProcedure() {
  const invalidate = useInvalidateRecordProcedures();
  return useMutation({
    mutationFn: async (input: CreateRecordProcedureInput) => {
      const res = await api.post<DetailResponse>('/settings/record-procedures', input);
      return res.data.data;
    },
    onSuccess: () => invalidate(),
  });
}

/**
 * `recordType`/`lifecycleEvent`/`kind` NIEEDYTOWALNE po utworzeniu — celowo brak tych polí.
 * `timeoutMinutes` wolno wysłać tylko dla procedury „Na przycisk" (inaczej 400 TIMEOUT_NOT_ALLOWED).
 */
export interface UpdateRecordProcedureInput {
  name?: string;
  description?: string | null;
  sql?: string;
  declaredParams?: RecordProcedureParam[];
  timeoutMinutes?: number;
}

export function useUpdateRecordProcedure() {
  const invalidate = useInvalidateRecordProcedures();
  return useMutation({
    mutationFn: async ({ id, values }: { id: string; values: UpdateRecordProcedureInput }) => {
      const res = await api.patch<DetailResponse>(`/settings/record-procedures/${id}`, values);
      return res.data.data;
    },
    onSuccess: (procedure) => invalidate(procedure.id),
  });
}

export function useDeleteRecordProcedure() {
  const invalidate = useInvalidateRecordProcedures();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/settings/record-procedures/${id}`);
      return id;
    },
    onSuccess: (id) => invalidate(id),
  });
}

export function useRestoreRecordProcedure() {
  const invalidate = useInvalidateRecordProcedures();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.post(`/settings/record-procedures/${id}/restore`);
      return id;
    },
    onSuccess: (id) => invalidate(id),
  });
}

// ============================================================================
// Hooks — test-sql (próbne wykonanie)
// ============================================================================

/**
 * Kontekst rezerwowy silnika (mirror `RecordProcedureTestContextSchema`,
 * `apps/api/lib/validators/record-procedures.ts`). `recordId`/`companyId` opcjonalne+nullable
 * (recordId `null` = symulacja tworzenia); `userId` MUSI być UUID; `fields`/`additional` to
 * text-JSON (autor SQL rzutuje `(:fields)::jsonb`) — surowy string JSON, nie obiekt.
 */
export interface RecordProcedureTestContext {
  recordId?: string | null;
  recordType: string;
  userId: string;
  companyId?: string | null;
  /**
   * Klasa dokumentu (`:record_class`, np. `purchase_invoice`) — wysyłana TYLKO dla typu `document` z
   * momentem „Na przycisk": procedura przycisku na dokumencie może zależeć od klasy, a przy prawdziwym
   * uruchomieniu serwer podstawia klasę strony. Dla pozostałych typów i momentów pole się nie wysyła.
   */
  recordClass?: string | null;
  fields?: string | null;
  additional?: string | null;
}

export interface TestRecordProcedureSqlInput {
  sql: string;
  lifecycleEvent: RecordLifecycleEvent;
  declaredParams: RecordProcedureParam[];
  /** Przykładowe wartości zadeklarowanych parametrów NIE-rezerwowych (opcjonalne). */
  paramValues?: Record<string, string | number | boolean | null>;
  procContext: RecordProcedureTestContext;
}

/**
 * Wynik akcji (saved/button): `message` niesie tylko procedura „Na przycisk" (tekst z kolumny
 * `message` pierwszego wiersza wyniku; `null`, gdy zapytanie go nie zwróciło) — przy `saved`
 * serwer pola nie wysyła, więc po stronie klienta zawsze `null`.
 */
export type TestRecordProcedureSqlResult =
  | { kind: 'validation'; severity: 'ok' | 'warning' | 'error'; message: string | null }
  | { kind: 'action'; rowCount: number; message: string | null };

interface ValidationTestResponse {
  severity: 'ok' | 'warning' | 'error';
  message: string | null;
}

interface ActionTestResponse {
  rowCount: number;
  message?: string | null;
}

/**
 * Mutacja „Testuj" — `POST /api/settings/record-procedures/test-sql`. Odpowiedź serwera NIE
 * niesie `kind` (mirror `test-sql/route.ts`: `{severity,message}`, `{rowCount}` albo — dla
 * przycisku — `{rowCount,message}`) — dokładamy go po stronie klienta na podstawie
 * `input.lifecycleEvent` (saved/button → action; control/delete → validation, mirror
 * backendowego `recordProcedureKind`). Test przycisku ma na serwerze stały limit 25 s;
 * przekroczenie wraca jako 422 EXECUTION_FAILED z gotowym polskim komunikatem.
 */
export function useTestRecordProcedureSql() {
  return useMutation({
    mutationFn: async (
      input: TestRecordProcedureSqlInput,
    ): Promise<TestRecordProcedureSqlResult> => {
      if (input.lifecycleEvent === 'saved' || input.lifecycleEvent === 'button') {
        const res = await api.post<ActionTestResponse>(
          '/settings/record-procedures/test-sql',
          input,
        );
        return { kind: 'action', rowCount: res.data.rowCount, message: res.data.message ?? null };
      }
      const res = await api.post<ValidationTestResponse>(
        '/settings/record-procedures/test-sql',
        input,
      );
      return { kind: 'validation', severity: res.data.severity, message: res.data.message };
    },
  });
}

// ============================================================================
// Hooks — lista procedur „Na przycisk" dla kreatora układu (klocek „Przycisk SQL")
// ============================================================================

/**
 * Pozycja lekkiej listy procedur „Na przycisk" (`GET /api/record-procedures/button-options`) —
 * bez treści SQL i parametrów. `active: false` = procedura usunięta (kreator pokazuje wtedy
 * znacznik „Procedura usunięta", a do wyboru oferuje wyłącznie aktywne).
 */
export interface RecordProcedureButtonOption {
  id: string;
  name: string;
  active: boolean;
}

/**
 * Dla której kartoteki pytamy o procedury. `recordClass` serwer wymaga TYLKO dla `document`
 * (klasa dokumentu) i zabrania dla pozostałych typów — nie zawęża listy, ale walidacja jest twarda.
 */
export interface RecordProcedureButtonOptionsTarget {
  recordType: string;
  recordClass?: string;
}

interface ButtonOptionsResponse {
  data: RecordProcedureButtonOption[];
}

/**
 * Lista należy do przestrzeni kluczy `['record-procedures', …]`, więc każda mutacja katalogu
 * (nowa, edytowana, usunięta, przywrócona procedura) odświeża też ją.
 */
export const recordProcedureButtonOptionsKey = (target: RecordProcedureButtonOptionsTarget) =>
  [
    ...RECORD_PROCEDURES_LIST_KEY,
    'button-options',
    target.recordType,
    target.recordClass ?? null,
  ] as const;

/**
 * `GET /api/record-procedures/button-options` — wymaga prawa edycji układu
 * (`crm_core.system_modules.manage`), NIE prawa katalogu procedur: bez niego 403
 * (`buttonOptionsErrorStatus` rozpoznaje ten przypadek). `target === undefined` = zapytanie
 * wyłączone (kartoteka nieznana kreatorowi).
 */
export function useRecordProcedureButtonOptions(
  target: RecordProcedureButtonOptionsTarget | undefined,
) {
  return useQuery<RecordProcedureButtonOption[]>({
    queryKey:
      target === undefined
        ? ([...RECORD_PROCEDURES_LIST_KEY, 'button-options', null] as const)
        : recordProcedureButtonOptionsKey(target),
    queryFn: async () => {
      if (target === undefined) {
        // `enabled` poniżej nie dopuszcza tej ścieżki — jawny błąd zamiast zapytania bez typu.
        throw new Error('useRecordProcedureButtonOptions: brak kartoteki');
      }
      const search = new URLSearchParams({ recordType: target.recordType });
      if (target.recordClass) search.set('recordClass', target.recordClass);
      const res = await api.get<ButtonOptionsResponse>(
        `/record-procedures/button-options?${search.toString()}`,
      );
      return res.data.data;
    },
    enabled: target !== undefined,
    retry: false,
    staleTime: 30_000,
  });
}

/** Status HTTP odpowiedzi serwera z błędu zapytania (`null` — błąd bez odpowiedzi, np. sieć). */
export function buttonOptionsErrorStatus(error: unknown): number | null {
  return isAxiosError(error) ? (error.response?.status ?? null) : null;
}
